import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Checkbox,
  FormControl,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'
import { groupBy, uniq } from 'lodash'

import Button from './Button'

const useStyles = makeStyles(theme => ({
  button: {
    justifyContent: 'space-between',
  },
  endIcon: {
    alignItems: 'center',
    display: 'flex',
  },
  groupLabel: {
    marginBottom: 0,
    padding: theme.spacing(2, 2, 1),
  },
  labelCount: {
    backgroundColor: theme.palette.purple.main,
    borderRadius: theme.shape.borderRadius,
    color: 'white',
    fontFamily: 'Gotham',
    height: 24,
    lineHeight: '24px',
    width: 24,
  },
}))

const Select = ({
  allowNoValue,
  className,
  clearText,
  fullWidth,
  groupOptionsBy,
  labelClassName,
  margin,
  multiple,
  onChange,
  options,
  placeholder,
  value,
  ...props
}) => {
  const classes = useStyles()

  // Menu
  const [anchorEl, setAnchorEl] = React.useState(null)
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Value changes
  const handleItemClick = optionValue => {
    let newValue
    if (multiple) {
      if (value.includes(optionValue)) {
        newValue = value.filter(v => v !== optionValue)
      } else {
        newValue = [...value, optionValue]
      }
      handleChange(newValue)
    } else {
      handleChange(optionValue)
      handleClose()
    }
  }
  const handleClearAll = () => {
    handleChange(multiple ? [] : '')
    handleClose()
  }
  const handleChange = value => {
    onChange({
      target: {
        value,
        name: props.name,
      },
    })
  }

  const makeLabel = () => {
    if (!multiple && value) {
      return options.find(o => o.value === value).label
    } else {
      return placeholder
    }
  }
  const labelCount = multiple ? value.length : null

  const label = makeLabel()
  const open = Boolean(anchorEl)

  const groups = groupOptionsBy
    ? groupOptions(options, groupOptionsBy)
    : undefined

  return (
    <FormControl className={className} fullWidth={fullWidth} margin={margin}>
      <Button
        className={classes.button}
        endIcon={
          <div className={classes.endIcon}>
            {labelCount ? (
              <Typography variant="body2" className={classes.labelCount}>
                {labelCount}
              </Typography>
            ) : null}
            <ExpandMore />
          </div>
        }
        onClick={handleClick}
        {...props}
      >
        <span className={labelClassName}>{label}</span>
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {allowNoValue && (
          <MenuItem button onClick={handleClearAll}>
            <ListItemText primary={clearText} />
          </MenuItem>
        )}
        {groups
          ? groups.map(({ label, options }, idx) => [
              [
                <Typography className={classes.groupLabel} variant="h5">
                  {label}
                </Typography>,
              ],
              options.map(option => (
                <Option
                  {...option}
                  key={option.value}
                  multiple={multiple}
                  onItemClick={handleItemClick}
                  selectedValue={value}
                />
              )),
            ])
          : options.map(option => (
              <Option
                {...option}
                key={option.value}
                multiple={multiple}
                onItemClick={handleItemClick}
                selectedValue={value}
              />
            ))}
      </Menu>
    </FormControl>
  )
}

const Option = ({ label, multiple, onItemClick, selectedValue, value }) => {
  return (
    <MenuItem button onClick={() => onItemClick(value)}>
      {multiple && (
        <Checkbox
          edge="start"
          checked={selectedValue.includes(value)}
          tabIndex={-1}
          disableRipple
        />
      )}
      <ListItemText primary={label} />
    </MenuItem>
  )
}

const groupOptions = (options, groupOptionsBy) => {
  const byGroup = groupBy(options, s => s[groupOptionsBy])
  const groupNames = uniq(options.map(s => s[groupOptionsBy]))
  const groupedOptions = groupNames.map(group => ({
    label: group,
    options: byGroup[group],
  }))
  return groupedOptions
}

Select.defaultProps = {
  allowNoValue: true,
  clearText: 'Clear',
  color: 'lightPurple',
  variant: 'contained',
}

export default Select
